import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolveMediaUrl } from '../utils/mediaUtils';
import { isPublicRoute } from '../utils/themeUtils';

let deferredPrompt = null;

export default function PWAInstallPrompt() {
  const { user } = useAuth();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem('institute_branding');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handleBranding = (e) => {
      if (e.detail) setBranding(e.detail);
      else {
        try {
          const cached = localStorage.getItem('institute_branding');
          setBranding(cached ? JSON.parse(cached) : null);
        } catch {}
      }
    };
    window.addEventListener('institute-branding-updated', handleBranding);
    return () => window.removeEventListener('institute-branding-updated', handleBranding);
  }, []);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const dismissed = sessionStorage.getItem('pwa-dismissed');
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show iOS prompt after 3 seconds if on Safari and not installed
    if (ios && !navigator.standalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowPrompt(false);
      }
      deferredPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('pwa-dismissed', 'true');
  };

  if (!showPrompt) return null;
  // People scanning a Google review QR are not app users; the banner would cover the page.
  if (location.pathname.startsWith('/r/')) return null;

  // Offer the install only after sign-in, on the portal: the page then carries the
  // institute's manifest, so the installed app gets the institute's name and icon.
  const isPublic = isPublicRoute(location.pathname);
  if (!user || isPublic) return null;
  const activeName = (!isPublic && (branding?.name || user?.institute_name)) || 'CoachHub';
  const rawLogo = !isPublic ? (branding?.favicon || branding?.logo || user?.institute_logo) : null;
  // Show the same icon the installed app will get (uploaded app icon, else the logo).
  const instituteId = !isPublic ? (branding?.institute_id || user?.institute_id) : null;
  const activeLogo = instituteId && process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/institute/${instituteId}/app-icon/192.png`
    : (rawLogo ? resolveMediaUrl(rawLogo) : null);
  const primaryColor = (!isPublic && (branding?.primary_color || user?.institute_primary_color)) || '#4F46E5';

  return (
    <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-80 z-[60] animate-slide-up" data-testid="pwa-install-prompt">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-4 relative">
        <button onClick={handleDismiss} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400" data-testid="pwa-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          {activeLogo ? (
            <img
              src={activeLogo}
              alt={activeName}
              className="w-12 h-12 rounded-xl object-contain bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div
              style={{ backgroundColor: primaryColor }}
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md text-white font-bold"
            >
              <Smartphone className="w-6 h-6 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm truncate">Install {activeName} App</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
              {isIOS
                ? 'Tap the Share button and then "Add to Home Screen"'
                : 'Install for quick access, works offline too!'}
            </p>
          </div>
        </div>
        {!isIOS && (
          <button
            onClick={handleInstall}
            data-testid="pwa-install-btn"
            style={{ backgroundColor: primaryColor }}
            className="w-full mt-3 flex items-center justify-center gap-2 text-white text-sm font-semibold py-2.5 rounded-xl hover:opacity-95 transition-opacity active:scale-95 shadow-md"
          >
            <Download className="w-4 h-4" /> Install App
          </button>
        )}
      </div>
    </div>
  );
}
